import React from 'react';
import { Save, Eye, Grid, ArrowLeft, Loader2, Lock, ChevronDown, ChevronUp, Edit2, GitBranch, PlayCircle, Magnet, Ruler, Crosshair, Maximize2, Undo2, Redo2, PanelLeft, PanelRight, PanelBottom, Settings as SettingsIcon } from 'lucide-react';

const BuilderToolbar = ({
  reviewReadOnly = false,
  // True only in the approver review flow — drives the "Pending Review" badge.
  // Edit controls are gated by reviewReadOnly, which also carries the edit lock
  // (FECOA-1302), so a lock non-holder gets the read-only chrome without the
  // review-specific badge.
  pendingReview = false,
  onBack,
  localAssessmentConfig,
  editingAssessmentName,
  assessmentNameValue,
  setAssessmentNameValue,
  saveAssessmentName,
  cancelEditingAssessmentName,
  startEditingAssessmentName,
  saveStatus,
  lastSaved,
  showGrid,
  setShowGrid,
  snapToGrid,
  setSnapToGrid,
  showAlignmentGuides,
  setShowAlignmentGuides,
  showPositionOverlay,
  setShowPositionOverlay,
  showOutOfBoundsWarning,
  fitElementsToCanvas,
  sidebarPosition,
  setSidebarPosition,
  saving,
  handleSave,
  showSaveDropdown,
  setShowSaveDropdown,
  enterPreviewMode,
  currentScreenIndex,
  showPreviewDropdown,
  setShowPreviewDropdown,
  showVersionsDropdown,
  setShowVersionsDropdown,
  canUndo = false,
  canRedo = false,
  onUndo = () => {},
  onRedo = () => {},
  onOpenSettings
}) => {
  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200/80 dark:border-white/[0.06] px-4 py-2.5 flex items-center justify-between gap-4 flex-shrink-0">
      {/* Left Section */}
      <div className="flex min-w-0 shrink items-center space-x-3">
        <button
          onClick={onBack}
          className="btn btn-ghost btn-icon"
          title="Back"
        >
          <ArrowLeft className="w-[18px] h-[18px]" />
        </button>
        {!reviewReadOnly && (
          <>
            <div className="border-l border-gray-200 dark:border-white/10 h-6"></div>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={onUndo}
                disabled={!canUndo}
                className="btn btn-ghost btn-icon disabled:opacity-40"
                title="Undo (Ctrl+Z)"
                aria-label="Undo"
              >
                <Undo2 className="w-[18px] h-[18px]" />
              </button>
              <button
                type="button"
                onClick={onRedo}
                disabled={!canRedo}
                className="btn btn-ghost btn-icon disabled:opacity-40"
                title="Redo (Ctrl+Y or Ctrl+Shift+Z)"
                aria-label="Redo"
              >
                <Redo2 className="w-[18px] h-[18px]" />
              </button>
            </div>
          </>
        )}
        <div className="border-l border-gray-200 dark:border-white/10 h-6"></div>

        {/* Editable Assessment Name */}
        <div className="flex items-center group">
          {editingAssessmentName ? (
            <input
              type="text"
              value={assessmentNameValue}
              onChange={(e) => setAssessmentNameValue(e.target.value)}
              onBlur={saveAssessmentName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveAssessmentName();
                } else if (e.key === 'Escape') {
                  cancelEditingAssessmentName();
                }
              }}
              autoFocus
              className="input h-9 w-auto font-medium"
              style={{ minWidth: '200px' }}
            />
          ) : (
            <>
              <h1
                className={`text-[15px] font-semibold tracking-tight text-gray-900 dark:text-gray-50 transition-colors ${reviewReadOnly ? '' : 'cursor-pointer hover:text-primary-700 dark:hover:text-primary-400'}`}
                onDoubleClick={reviewReadOnly ? undefined : startEditingAssessmentName}
                title={reviewReadOnly ? undefined : 'Double-click to rename assessment'}
              >
                {localAssessmentConfig?.name || 'Assessment Builder'}
              </h1>
              {pendingReview && (
                <span className="badge badge-warning ml-2.5">
                  <Lock className="w-3 h-3" />
                  Pending Review — editing locked
                </span>
              )}
              {localAssessmentConfig?.publication_status === 'published' && (
                <span className="badge badge-success ml-2.5">
                  <Lock className="w-3 h-3" />
                  Published v{localAssessmentConfig?.version || '1.0'}
                </span>
              )}
              {!reviewReadOnly && localAssessmentConfig?.publication_status !== 'published' && (
                <button
                  onClick={startEditingAssessmentName}
                  className="btn btn-ghost btn-sm btn-icon ml-1 opacity-0 group-hover:opacity-100"
                  title="Rename assessment"
                >
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
              )}
            </>
          )}
        </div>
        
        {/* Save Status */}
        {!reviewReadOnly && (
        <div className="flex items-center ml-1">
          {saveStatus === 'saving' && (
            <div className="badge badge-gray">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span>Saving...</span>
            </div>
          )}
          {saveStatus === 'saved' && lastSaved && (
            <div className="badge badge-dot badge-success">
              <span>Saved</span>
            </div>
          )}
          {saveStatus === 'unsaved' && (
            <div className="badge badge-dot badge-warning">
              <span>Unsaved</span>
            </div>
          )}
        </div>
        )}
      </div>

      {/* Center — canvas tools (device preset lives in Layout tab) */}
      <div className="flex flex-1 justify-center items-center min-w-0">
        {!reviewReadOnly && (
        <div className="flex items-center gap-2">
        <span className="hidden text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500 xl:inline">Canvas</span>
        <div className="segmented">
          <label
            className={`p-1.5 rounded-md cursor-pointer transition-colors ${showGrid ? 'bg-white text-primary-700 shadow-xs ring-1 ring-gray-900/[0.04] dark:bg-gray-700 dark:text-primary-300 dark:ring-white/10' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'}`}
            title="Toggle Grid"
          >
            <input
              type="checkbox"
              checked={showGrid}
              onChange={(e) => setShowGrid(e.target.checked)}
              className="sr-only"
            />
            <Grid className="w-4 h-4" />
            <span className="sr-only">Grid</span>
          </label>
          <label 
            className={`p-1.5 rounded-md cursor-pointer transition-colors ${snapToGrid ? 'bg-white text-primary-700 shadow-xs ring-1 ring-gray-900/[0.04] dark:bg-gray-700 dark:text-primary-300 dark:ring-white/10' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'}`}
            title="Toggle Snap to Grid"
          >
            <input
              type="checkbox"
              checked={snapToGrid}
              onChange={(e) => setSnapToGrid(e.target.checked)}
              className="sr-only"
            />
            <Magnet className="w-4 h-4" />
            <span className="sr-only">Snap to grid</span>
          </label>
          <label 
            className={`p-1.5 rounded-md cursor-pointer transition-colors ${showAlignmentGuides ? 'bg-white text-primary-700 shadow-xs ring-1 ring-gray-900/[0.04] dark:bg-gray-700 dark:text-primary-300 dark:ring-white/10' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'}`}
            title="Toggle Alignment Guides"
          >
            <input
              type="checkbox"
              checked={showAlignmentGuides}
              onChange={(e) => setShowAlignmentGuides(e.target.checked)}
              className="sr-only"
            />
            <Ruler className="w-4 h-4" />
            <span className="sr-only">Alignment guides</span>
          </label>
          <label 
            className={`p-1.5 rounded-md cursor-pointer transition-colors ${showPositionOverlay ? 'bg-white text-primary-700 shadow-xs ring-1 ring-gray-900/[0.04] dark:bg-gray-700 dark:text-primary-300 dark:ring-white/10' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'}`}
            title="Toggle Position Overlay"
          >
            <input
              type="checkbox"
              checked={showPositionOverlay}
              onChange={(e) => setShowPositionOverlay(e.target.checked)}
              className="sr-only"
            />
            <Crosshair className="w-4 h-4" />
            <span className="sr-only">Position overlay</span>
          </label>
          
          {/* Fit to Canvas Button */}
          <button
            onClick={() => fitElementsToCanvas('proportional')}
            className={`p-1.5 rounded-md transition-colors ${
              showOutOfBoundsWarning
                ? 'bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/25'
                : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100'
            }`}
            title={showOutOfBoundsWarning 
              ? "Elements are outside canvas bounds - click to auto-fit" 
              : "Fit all elements within canvas bounds"}
          >
            <Maximize2 className="w-4 h-4" />
            <span className="sr-only">Fit to canvas</span>
          </button>
        </div>
        </div>
        )}
      </div>

      {/* Right Section - Actions */}
      <div className="flex shrink-0 items-center space-x-2">
        {/* Sidebar Position Controls (hidden in review — no sidebars) */}
        {!reviewReadOnly && (
        <div className="flex items-center gap-2 mr-1 border-r border-gray-200 dark:border-white/10 pr-3">
          <span className="hidden text-[11px] font-bold uppercase tracking-wide text-gray-400 dark:text-gray-500 xl:inline">Panel</span>
          <div className="segmented">
            <button
              onClick={() => setSidebarPosition('left')}
              className={`segmented-item px-2 py-1.5 ${
                sidebarPosition === 'left' ? 'segmented-item-active' : ''
              }`}
              title="Left Sidebar"
              aria-label="Show the panel on the left"
            >
              <PanelLeft className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setSidebarPosition('right')}
              className={`segmented-item px-2 py-1.5 ${
                sidebarPosition === 'right' ? 'segmented-item-active' : ''
              }`}
              title="Right Sidebar"
              aria-label="Show the panel on the right"
            >
              <PanelRight className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setSidebarPosition('bottom')}
              className={`segmented-item px-2 py-1.5 ${
                sidebarPosition === 'bottom' ? 'segmented-item-active' : ''
              }`}
              title="Bottom Panel"
              aria-label="Show the panel at the bottom"
            >
              <PanelBottom className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        )}

        {!reviewReadOnly && onOpenSettings && (
          <button
            type="button"
            onClick={onOpenSettings}
            className="btn btn-ghost btn-icon"
            title="Assessment settings"
            aria-label="Assessment settings"
          >
            <SettingsIcon className="w-4 h-4" />
          </button>
        )}

        {/* Save split button. Publishing was intentionally dropped (handled by
            the review/approval workflow); the draft + snapshot options were
            requested back (Husain). Versioning is due for a cleanup. */}
        {!reviewReadOnly && (
        <div className="relative inline-flex">
          {/* Main Save Button - quick save without a version snapshot */}
          <button
            onClick={() => handleSave(true, false)}
            disabled={saving}
            className="btn btn-primary btn-sm rounded-r-none"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? 'Saving...' : 'Save'}
          </button>

          {/* Dropdown Arrow Button */}
          <button
            onClick={() => setShowSaveDropdown(!showSaveDropdown)}
            disabled={saving}
            className="btn btn-primary btn-sm rounded-l-none border-l border-white/20 px-2"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>

          {showSaveDropdown && (
            <>
              {/* Backdrop to close dropdown */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowSaveDropdown(false)}
              />

              {/* Dropdown Menu */}
              <div className="absolute right-0 top-full mt-1 w-56 rounded-xl border border-gray-200 bg-white p-1.5 shadow-float dark:border-white/10 dark:bg-gray-800 z-50">
                <button
                  onClick={() => {
                    handleSave(true, false);
                    setShowSaveDropdown(false);
                  }}
                  disabled={saving}
                  className="w-full text-left rounded-lg px-2.5 py-2 text-[13.5px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 flex items-center gap-2.5"
                  title="Quick save your current work without creating a version history entry"
                >
                  <Save className="w-4 h-4 text-gray-400" />
                  Save as Draft
                </button>

                <button
                  onClick={async () => {
                    await handleSave(false, true);
                    setShowSaveDropdown(false);
                  }}
                  disabled={saving}
                  className="w-full text-left rounded-lg px-2.5 py-2 text-[13.5px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:text-white disabled:opacity-50 flex items-center gap-2.5"
                  title="Save and create a restorable backup in Version History"
                >
                  <GitBranch className="w-4 h-4 text-gray-400" />
                  Save &amp; Create Snapshot
                </button>
              </div>
            </>
          )}
        </div>
        )}

        <div className="relative inline-flex">
          <button
            onClick={() => enterPreviewMode(0)}
            className="btn btn-secondary btn-sm rounded-r-none"
            title="Preview from beginning"
          >
            <Eye className="w-3.5 h-3.5" />
            Preview
          </button>
          <button
            onClick={() => setShowPreviewDropdown(!showPreviewDropdown)}
            className="btn btn-secondary btn-sm -ml-px rounded-l-none px-2"
          >
            <ChevronDown className="w-3.5 h-3.5" />
          </button>
          {showPreviewDropdown && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowPreviewDropdown(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-48 rounded-xl border border-gray-200 bg-white p-1.5 shadow-float dark:border-white/10 dark:bg-gray-800 z-20">
                <button
                  onClick={() => {
                    enterPreviewMode(0);
                    setShowPreviewDropdown(false);
                  }}
                  className="w-full text-left rounded-lg px-2.5 py-2 text-[13.5px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:text-white flex items-center gap-2.5"
                >
                  <PlayCircle className="w-4 h-4 text-gray-400" />
                  From Beginning
                </button>
                <button
                  onClick={() => {
                    enterPreviewMode(currentScreenIndex);
                    setShowPreviewDropdown(false);
                  }}
                  className="w-full text-left rounded-lg px-2.5 py-2 text-[13.5px] text-gray-700 dark:text-gray-300 hover:bg-gray-100 hover:text-gray-900 dark:hover:text-white flex items-center gap-2.5"
                >
                  <Eye className="w-4 h-4 text-gray-400" />
                  From Current Screen
                </button>
              </div>
            </>
          )}
        </div>

        {/* Version History button - Toggle dropdown */}
        {!reviewReadOnly && (
        <button
          onClick={() => setShowVersionsDropdown(!showVersionsDropdown)}
          className={`btn btn-secondary btn-sm ${
            showVersionsDropdown ? 'border-gray-300 bg-gray-50 dark:border-white/15 dark:bg-gray-700' : ''
          }`}
          title="View version history and restore previous versions"
        >
          <GitBranch className="w-3.5 h-3.5" />
          Versions
          {showVersionsDropdown ? <ChevronUp className="w-3.5 h-3.5 -ml-0.5" /> : <ChevronDown className="w-3.5 h-3.5 -ml-0.5" />}
        </button>
        )}
      </div>
    </div>
  );
};

export default BuilderToolbar;
